'use client';
import React, { createContext, useContext, useMemo } from 'react';

export type SubmittedRating = 'positive' | 'negative';

export type FeedbackActionType = 'positive' | 'negative' | 'general';

export type InlineFeedbackState = {
  activeTraceId: string | null;
  text: string;
  submitError: string | null;
  isSubmitting: boolean;
  hasPendingRating: boolean;
};

export type InlineFeedbackHandlers = {
  onTextChange: (text: string) => void;
  onSubmit: () => void;
  onSkip: () => void;
  onCancel: () => void;
};

export type ChatFeedbackContextValue = {
  isEnabled: boolean;
  getTraceSubmittingState: (traceId: string) => boolean;
  getTraceSubmittedRating: (traceId: string) => SubmittedRating | undefined;
  onFeedbackClick: (params: {
    traceId: string;
    actionType: FeedbackActionType;
  }) => void;
  inlineFeedback: InlineFeedbackState;
  inlineFeedbackHandlers: InlineFeedbackHandlers;
};

const ChatFeedbackContext = createContext<ChatFeedbackContextValue | undefined>(
  undefined,
);

type ChatFeedbackProviderProps = {
  children: React.ReactNode;
  isEnabled: boolean;
  submittingTraces: Record<string, boolean>;
  submittedRatings: Record<string, SubmittedRating>;
  onFeedbackClick: (params: {
    traceId: string;
    actionType: FeedbackActionType;
  }) => void;
  inlineFeedback: InlineFeedbackState;
  inlineFeedbackHandlers: InlineFeedbackHandlers;
};

export function ChatFeedbackProvider({
  children,
  isEnabled,
  submittingTraces,
  submittedRatings,
  onFeedbackClick,
  inlineFeedback,
  inlineFeedbackHandlers,
}: ChatFeedbackProviderProps) {
  const contextValue = useMemo<ChatFeedbackContextValue>(
    () => ({
      isEnabled,
      getTraceSubmittingState: (traceId: string) =>
        submittingTraces[traceId] ?? false,
      getTraceSubmittedRating: (traceId: string) => submittedRatings[traceId],
      onFeedbackClick,
      inlineFeedback,
      inlineFeedbackHandlers,
    }),
    [
      isEnabled,
      submittingTraces,
      submittedRatings,
      onFeedbackClick,
      inlineFeedback,
      inlineFeedbackHandlers,
    ],
  );

  return (
    <ChatFeedbackContext.Provider value={contextValue}>
      {children}
    </ChatFeedbackContext.Provider>
  );
}

export function useChatFeedback(): ChatFeedbackContextValue {
  const context = useContext(ChatFeedbackContext);
  if (context === undefined) {
    throw new Error(
      'useChatFeedback must be used within a ChatFeedbackProvider',
    );
  }
  return context;
}
